import {AfterViewInit, Component, Input, OnDestroy, ViewChild} from '@angular/core';
import {ReplaySubject} from 'rxjs';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthService} from '../../../shared/services/auth.service';
import {delay, distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../shared/interface/response/counted-and-paginated';
import {DocumentAddDialogComponent} from '../../documents/document-add-dialog/document-add-dialog.component';
import {
  DICT_TYPE_KRK_STATUS,
  DictService,
  FILE_TYPE_KRK,
  KRK_STATUS_PENDING
} from '../../../shared/services/dict.service';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import {Krk} from '../../../shared/interface/krk';
import {KrkService} from '../../../shared/services/krk.service';
import {FilterService} from '../../../shared/services/filter.service';
import {ActivatedRoute, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../shared/ngrx/route-serializer';
import {MatSlideToggleChange} from "@angular/material/slide-toggle";
import {HttpErrorResponse} from "@angular/common/http";
import {Uuid} from "../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";

@Component({
  selector: 'app-all-krk-list',
  templateUrl: './all-krk-list.component.html',
  styleUrls: ['./all-krk-list.component.scss']
})
export class AllKrkListComponent implements AfterViewInit, OnDestroy {
  @Input() filterPending = false;
  @Input() embeddedView = false;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild('paginator') paginator: MatPaginator;

  initialFilters: any;
  krks: Krk[] = [];
  displayedColumns: string[] = ['name', 'ofwca', 'status', 'activeTo', 'daysLeft', 'payer', 'activeFee', 'partner',
    'assetsRegional', 'actions', ];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  pendingStatusId?: number;
  updateInProgress = false;


  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private krkService: KrkService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private dictService: DictService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.krkService.getKrkListSubject()
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<Krk>) => {
          this.krks = e.items;
          this.totalData = e.pagination.total;
          this.updateInProgress = false;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      gte$createdAt: 'date',
      lte$createdAt: 'date',
      ofwca: 'string',
      status: 'int',
      gte$activeTo: 'date',
      lte$activeTo: 'date',
      gte$activeFrom: 'date',
      lte$activeFrom: 'date',
      payer: 'int',
      partner: 'string',
      ofwcaAssetsRegional: 'string',
      activeFee: 'int',
    }, filters);
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
        if (this.embeddedView) {
          this.loadListData();
        } else {
          this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
            page: event.pageIndex,
            pageSize: event.pageSize,
            ...this.filtersForm.value
          })]);
        }
      });

    if (this.filterPending) {
      this.dictService.getId(KRK_STATUS_PENDING, DICT_TYPE_KRK_STATUS)
        .subscribe((dictId: number) => {
          this.pendingStatusId = dictId;
          this.subscribeQueryParams();
        });
    } else {
      this.subscribeQueryParams();
    }
  }

  subscribeQueryParams(): void {
    if (!this.embeddedView) {
      this.store
        .pipe(
          select(getCurrentRouteState),
          delay(0),
          distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
          takeUntil(this.destroyed$),
        )
        .subscribe((state: RouterStateUrl) => {
          this.parseFilters(state.params);
          this.paginator.pageIndex = state.params.page;
          this.paginator.pageSize = state.params.pageSize;
          this.loadListData();
        })
      ;
    } else {
      this.loadListData();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.krkService.loadList(this.paginator.pageIndex, this.paginator.pageSize, this.buildFilters());
  }

  buildFilters(): any {
    if (this.pendingStatusId) {
      return {
        ...this.initialFilters,
        status: this.pendingStatusId
      };
    } else {
      return this.initialFilters;
    }
  }

  filter(): void {
    if (this.embeddedView) {
      if (this.paginator.pageIndex === 0) {
        this.loadListData();
      } else {
        this.paginator.firstPage();
      }
    } else {
      if (this.paginator.pageIndex === 0) {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: this.paginator.pageIndex,
          pageSize: this.paginator.pageSize,
          ...this.filtersForm.value
        })]);
      } else {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: 0,
          pageSize: this.paginator.pageSize,
          ...this.filtersForm.value,
        })]);
      }
    }
  }


  downloadReport(): void {
    this.krkService.downloadReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    if (this.embeddedView) {
      this.loadListData();
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
        page: 0,
        pageSize: this.paginator.pageSize,
      }]);
    }
  }

  addDocument(krk: Krk): void {

    const dialogRef = this.dialog.open(DocumentAddDialogComponent, {
      width: '55%',
      data: {
        krkObject: krk,
        types: [FILE_TYPE_KRK],
      },
    });

    dialogRef.afterClosed().subscribe(result => {
    });
  }

  deleteKrk(krk: Krk): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć KRK?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.krkService.deleteKrk(krk.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto KRK', '', {duration: 2000});
            this.loadListData();
          });
      }
    });
  }

  agreedToDeregisterChanged($event: MatSlideToggleChange, krk: Krk): void {
    this.updateInProgress = true;

    this.krkService.updateKrkActiveFeeController(krk.uuid, {
      activeFee: $event.checked
    })
      .subscribe(() => {
        this.snack.open('Pomyślnie zakutalizowano krk', '', {duration: 2000});
        this.updateInProgress = false;
        krk.activeFee = $event.checked;
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy aktualizacji wystąpił błąd', error.status.toString(), {duration: 2000});
        this.updateInProgress = false;
      });
  }
}
